import { Autocomplete, Box, Button, CircularProgress, debounce, FormControl, MenuItem, Select, Switch, TextField, Typography } from "@mui/material";
import { FC, useEffect, useMemo, useState } from "react";
import { API_SLUGS } from "../../constants";
import useFetch from "../../hooks/http/useFetch";
import { axiosInstance } from "../../services/axios";
import ActionsComponent from "./Action";
import Condition, { isConditionValid } from "./Condition";
import { IAlertState, useAlertState } from "../../store/alertState";

const ACTION_OPTIONS: any = [
    {
        key: "ASSIGN_RISK",
        label: "Assign Risk",
        params: {
            risk: {
                type: "select",
                options: [
                    { key: "critical", label: "Critical" },
                    { key: "high", label: "High" },
                    { key: "medium", label: "Medium" },
                    { key: "low", label: "Low" },
                    { key: "information", label: "Information"}
                ]
            }
        }

    },
    {
        key: "CHANGE_STATUS",
        label: "Change Status",
        params: {
            status: {
                type: "select",
                options: [
                    {
                        key: "risk_accepted",
                        label: "Accept Risk"
                    },
                    {
                        key: "false_positive",
                        label: "False Positive"
                    },
                ]
            }
        }
    }

]

const AutomationsForm: FC<any> = ({ facts, commonFacts, updateOnly, updateUUID, defaultFormValues, callback }) => {
  const [selectedFact, setSelectedFact] = useState<any>(
    facts[0]
  );
  const [loading, setLoading] = useState<boolean>(false);
  const { setMessage } = useAlertState() as IAlertState;

  const [enabled, setEnabled] = useState(false);

  const actionOptions = [...ACTION_OPTIONS];

  // let { data: faultGroupData } = useFetch("/faultgroups");

  // const faultFacts = useMemo(() => {
  //   if (!faultGroupData) {
  //     return [];
  //   }
  //   const faults = faultGroupData?.faultGroups;
  //   let faultOptions = [];
  //   for (let fault of faults) {
  //     faultOptions.push({
  //       key: fault?.id,
  //       label: fault?.name
  //     });
  //   }

  //   if (faultOptions?.length) {
  //     return [{
  //       key: "fault_groups",
  //       label: "Fault Groups",
  //       type: "autoComplete",
  //       options: faultOptions
  //     }];
  //   } else return [];

  // }, [faultGroupData]);

  //const { data: groupData } = useFetch("/group");

  const groupFacts = [{
        key: "groupId",
        label: "Group",
        type: "custom",
        render: GroupBackendSearch,
        options: []
      }];
   

  const cweFacts = [{
        key: "faultId",
        label: "Fault Id",
        type: "custom",
        render: FaultIdBackendSearch,
        options: []
  }];
   
  commonFacts = [...groupFacts, ...cweFacts];


  const [name, setName] = useState("");
  const [condition, setCondition] = useState({
    any: []
  });

  const [actions, setActions] = useState<any>([]);

  useEffect(() => {
    if(updateOnly) {
      fetchFormData()
    }
  }, [])

  const fetchFormData = async() => {
    setLoading(true)
    try {
      const response = await axiosInstance.get(`/rules/${updateUUID}`);
      setSelectedFact(facts.find((fact: any) => fact?.key === response?.data?.details?.type));
      setName(response?.data?.name);
      setCondition(response?.data?.details?.conditions || {any: []})
      setActions(response?.data?.details?.event?.params?.actions || [])
      setEnabled(response?.data?.enabled)
    } catch (error) {
      
    } finally {
      setLoading(false)
    }
  }

  const onChangeActions = (newActions: any) => {
    setActions(newActions);
  };

  const onAddAction = () => {
    setActions([...actions, { type: '', params: [] }]);
  };

  const onDeleteAction = (index: number) => {
    const updatedActions = [...actions];
    updatedActions.splice(index, 1);
    setActions(updatedActions);
  };

  const handleSave = async () => {
    const details = {
      conditions: condition,
      event: {
        type: 'GUID',
        params: {
          actions: actions
        }
      }
    };

    if (updateOnly) {
      try {
        await axiosInstance.patch(`/rules/${updateUUID}`, {
          type: selectedFact?.key,
          name,
          enabled,
          details
        });
      } catch (error: any) {
        setMessage({title: error.response?.data?.message, type: "error"})
      }
     
    } else {
      try {
        await axiosInstance.post("/rules", {
          type: selectedFact?.key,
          name,
          enabled,
          details
        });
      } catch (error: any) {
        setMessage({title: error.response?.data?.message, type: "error"})
      }
      
    }
    if (callback) {
      callback();
    }
  };

  const isActionsValid = (actions: any[]) => {
    if (!actions.length) return true;
    return actions.every(action => action.type && action.type !== "");
  };

  const isFormValid =
    name.trim() !== "" &&
    selectedFact !== undefined &&
    isConditionValid(condition) &&
    isActionsValid(actions);

  return loading ? (
    <Box display="flex" justifyContent="center" alignItems="center">
      <CircularProgress />
    </Box>
  ): (
    <>
  <Box sx={{ maxHeight: 'calc(100vh - 64px)', overflowY: 'auto'}}>
    <Typography variant="h6" sx={{mb:1}}>Name</Typography>

    <TextField sx={{ mb: 2 }} size="small" value={name} onChange={(e) => setName(e.target.value)} />

    <Typography variant="h6">Enable</Typography>
    <Switch
      sx={{ mb: 2, ml:-1 }}
      checked={enabled}
      onChange={() => setEnabled(!enabled)}
    />
    <Typography variant="h6"sx={{mb:1}}>Entity</Typography>

    <FormControl sx={{ mb: 2 }}>


      <Select
        value={selectedFact}
        onChange={(e) => setSelectedFact(e.target.value)}
        sx={{ minWidth: "11em" }}
      >
        {
          facts.map((fact: any) => {
            return <MenuItem key={fact?.key} value={fact}>{fact?.label}</MenuItem>;
          })
        }
      </Select>
    </FormControl>

    <Typography variant="h6">Conditions</Typography>
    <Condition
      facts={[...selectedFact?.facts, ...commonFacts]}
      condition={condition}
      onChangeCondition={(newCondition: any) => {
        setCondition(newCondition);
      }}
    />
    <Typography variant="h6"sx={{mb:1}}>Actions</Typography>
    <ActionsComponent
      actionOptions={actionOptions}
      actions={actions}
      onChangeActions={onChangeActions}
      onAddAction={onAddAction}
      onDeleteAction={onDeleteAction}
    />
    
  </Box>
  <Button
      variant="contained"
      size="small"
      
      onClick={handleSave}
      sx={{
        position: 'sticky',
        bottom: 0,
        mt:2
      }}
      disabled={!isFormValid}
    >
      Save
    </Button>
  </>)
};

const GroupBackendSearch: FC<any> = ({ value = [], onChange }) => {
  const [backendSearchGroups, setBackendSearchGroups] = useState<any[]>([]);
  const [selectedGroups, setSelectedGroups] = useState<any[]>([]);
  const [loadingGroups, setLoadingGroups] = useState(false);
  const [inputValue, setInputValue] = useState("");

  const fetchGroups = async (searchQuery?: string | null) => {
    try {
      setLoadingGroups(true);
      const response = await axiosInstance.get(
        `/group?limit=30${searchQuery ? `&search=${encodeURIComponent(searchQuery)}` : ''}`
      );
      const newGroups = response.data?.groups || [];
      const mergedGroups = [...newGroups, ...selectedGroups];
      const uniqueGroupsMap = new Map();
      mergedGroups.forEach(group => {
        if (group?.id) {
          uniqueGroupsMap.set(group.id, group);
        }
      });

      setBackendSearchGroups(Array.from(uniqueGroupsMap.values()));

    } catch (error) {
      console.error("Error fetching groups:", error);
    } finally {
      setLoadingGroups(false);
    }
  };

  const debouncedFetchGroups = useMemo(() => {
    const handler = (query?: string | null) => {
      fetchGroups(query);
    };
    return debounce(handler, 300);
  }, [selectedGroups]);

  useEffect(() => {
    fetchGroups(null);
    return () => {
      //@ts-ignore
      debouncedFetchGroups.cancel();
    };
  }, []);

  const fetchGroupById = async (id: string) => {
    try {
      const response = await axiosInstance.get(`/group/${id}`);
      return response?.data;
    } catch (error) {
      console.error(`Error fetching group ${id}:`, error);
      return null;
    }
  };

  const ensureValueObjectsExist = async (valueIds: string[], knownGroups: any[]) => {
    const knownIds = knownGroups?.map((group) => group?.id);
    const missingIds = valueIds?.filter((id) => !knownIds?.includes(id));
    const missingGroups = await Promise.all(
      missingIds?.map((id) => fetchGroupById(id))
    );
    setSelectedGroups([...selectedGroups, ...missingGroups.filter(Boolean)]);
  };

  useEffect(() => {
    ensureValueObjectsExist(value, selectedGroups);
  }, []);

  useEffect(() => {
    // Ensuring selectedGroups includes all the  selected value
    const all = [...backendSearchGroups, ...selectedGroups];
    const mapped = value
      ?.map((id: any) => all?.find((group) => group?.id === id))
      ?.filter(Boolean);
    setSelectedGroups(mapped);
  }, [value, backendSearchGroups]);

  const handleGroupSearchChange = (_: any, newInputValue: string, reason: string) => {
    if (reason === "input") {
      setInputValue(newInputValue);
      debouncedFetchGroups(newInputValue);
    }
  };

  return (
    <Autocomplete
      multiple
      disableCloseOnSelect
      sx={{ maxWidth: "100%", textAlign: "left" }}
      size="small"
      getOptionLabel={(option) => option?.name || ""}
      isOptionEqualToValue={(opt, val) => opt?.id === val?.id}
      options={backendSearchGroups}
      value={selectedGroups}
      inputValue={inputValue}
      loading={loadingGroups}
      onInputChange={handleGroupSearchChange}
      onChange={(_, newSelected) => {
        setSelectedGroups(newSelected);
        setInputValue("");
        fetchGroups(null);
        onChange(newSelected?.map((value) => value?.id));
      }}
      forcePopupIcon={false}
      renderInput={(params) => (
        <TextField
          {...params}
          autoComplete="off"
          name="group"
          label="Search for Group"
          variant="outlined"
        />
      )}
    />
  );
};


const FaultIdBackendSearch: FC<any> = ({ value = [], onChange }) => {
  const [backendSearchFaultIds, setBackendSearchFaultIds] = useState<any[]>([]);
  const [selectedFaultIds, setSelectedFaultIds] = useState<any[]>([]);
  const [loadingFaultIds, setLoadingFaultIds] = useState(false);
  const [inputValue, setInputValue] = useState("");

  const fetchFaultIds = async (searchQuery?: string | null) => {
    try {
      setLoadingFaultIds(true);
      const response = await axiosInstance.get(
        `/faults?limit=30${searchQuery ? `&search=${encodeURIComponent(searchQuery)}` : ''}`
      );

      const prefixesToFilter = ["SIF-", "SCF-", "CWE-", "SFF-", "SPF-", "SDE-"];

    const filteredFaults = response.data?.faults.filter((item: any) =>
      prefixesToFilter.some(prefix => item?.id?.startsWith(prefix))
    );

      const mergedFaults = [...filteredFaults, ...selectedFaultIds];
      const uniqueGroupsMap = new Map();
      mergedFaults.forEach(fault => {
        if (fault?.id) {
          uniqueGroupsMap.set(fault.id, fault);
        }
      });

      setBackendSearchFaultIds(Array.from(uniqueGroupsMap.values()));
    } catch (error) {
      console.error("Error fetching fault Ids:", error);
    } finally {
      setLoadingFaultIds(false);
    }
  };

  const debouncedFetchFaultIds = useMemo(() => {
    const handler = (query?: string | null) => {
      fetchFaultIds(query);
    };
    return debounce(handler, 300);
  }, [selectedFaultIds]);

  useEffect(() => {
    fetchFaultIds(null);
    return () => {
      //@ts-ignore
      debouncedFetchGroups.cancel();
    };
  }, []);

  const fetchFaultIdById = async (id: string) => {
    try {
      const response = await axiosInstance.get(`/faults/${id}`);
      return response?.data;
    } catch (error) {
      console.error(`Error fetching fault Id ${id}:`, error);
      return null;
    }
  };

  const ensureValueObjectsExist = async (valueIds: string[], knownFaultIds: any[]) => {
    const knownIds = knownFaultIds?.map((fault) => fault?.id);
    const missingIds = valueIds?.filter((id) => !knownIds?.includes(id));

    const missingFaultIds = await Promise.all(
      missingIds?.map((id) => fetchFaultIdById(id))
    );

    setSelectedFaultIds([...selectedFaultIds, ...missingFaultIds.filter(Boolean)])
  };


  useEffect(() => {
    ensureValueObjectsExist(value, selectedFaultIds);
  }, []);

  useEffect(() => {
    // Ensuring selectedFaultIds includes all the  selected value
    const all = [...backendSearchFaultIds, ...selectedFaultIds];
    const mapped = value
      ?.map((id: any) => all?.find((fault) => fault?.id === id))
      ?.filter(Boolean);
    setSelectedFaultIds(mapped);
  }, [value, backendSearchFaultIds]);

  const handleFaultIdSearchChange = (_: any, newInputValue: string, reason: string) => {
    if (reason === 'input') {
      setInputValue(newInputValue);
      debouncedFetchFaultIds(newInputValue);
    } 
  };


  return (
    <Autocomplete
      multiple
      disableCloseOnSelect
      sx={{ maxWidth: "100%", textAlign: "left" }}
      size="small"
      getOptionLabel={(option) => option?.id || ""}
      isOptionEqualToValue={(opt, val) => opt?.id === val?.id}
      options={backendSearchFaultIds}
      value={selectedFaultIds}
      inputValue={inputValue}
      loading={loadingFaultIds}
      onInputChange={handleFaultIdSearchChange}
      onChange={(_, newSelected) => {
        setSelectedFaultIds(newSelected);
        setInputValue('')
        debouncedFetchFaultIds(null)
        onChange(newSelected?.map((value) => value?.id));
      }}
      forcePopupIcon={false}
      renderInput={(params) => (
        <TextField
          {...params}
          autoComplete="off"
          name="faultId"
          label="Search for Fault Id"
          variant="outlined"
        />
      )}
    />
  );
};

export default AutomationsForm;